import { formatDate } from "@/lib/utils";
import React from "react";
import DOMPurify from "dompurify";
import PropTypes from "prop-types";
import { Settings, Plus, Box, Package, Hash, DollarSign, FileText, Layers } from "lucide-react";
import Modal from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";

const PartsViewModal = ({
  show,
  onHide,
  selectedRepairJob,
  partsEntries,
  currencySymbol,
  repairJobs,
  handleAddParts,
  products,
}) => {
  return (
    <Modal
      open={show}
      onOpenChange={(v) => !v && onHide()}
      title={
        <div className="flex items-center gap-2">
          <span>Parts Used</span>
          <span className="text-xs font-normal text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
            {selectedRepairJob?.item_description}
          </span>
        </div>
      }
      icon={Settings}
      maxWidth="max-w-4xl"
      footer={
        <div className="flex justify-end w-full">
          <Button variant="outline" onClick={onHide}>
            Close
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {partsEntries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed rounded-xl bg-muted/30">
            <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <Package className="h-8 w-8 text-muted-foreground" />
            </div>
            <h5 className="text-lg font-semibold">No Parts Found</h5>
            <p className="text-sm text-muted-foreground max-w-xs mx-auto mb-6">
              No parts have been recorded for this repair job yet.
            </p>
            {(selectedRepairJob?.status?.value || selectedRepairJob?.status) !==
              "completed" && (
                <Button
                  onClick={() => {
                    onHide();
                    handleAddParts(selectedRepairJob);
                  }}
                  className="gap-2"
                >
                  <Plus size={16} /> Add First Part
                </Button>
              )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex justify-between items-center px-1">
              <h6 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">
                Inventory List ({partsEntries.length})
              </h6>
              {(selectedRepairJob?.status?.value ||
                selectedRepairJob?.status) !== "completed" && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 gap-2"
                    onClick={() => {
                      onHide();
                      handleAddParts(
                        repairJobs.find((job) => job.id === selectedRepairJob.id),
                      );
                    }}
                  >
                    <Plus size={14} /> Add New Part
                  </Button>
                )}
            </div>

            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-3">
                {partsEntries.map((entry, index) => {
                  const product = products.find(
                    (p) => p.id === entry.product_id,
                  );
                  const totalCost =
                    parseFloat(entry.quantity_used) *
                    parseFloat(entry.unit_cost || 0);

                  return (
                    <Card key={entry.id || index} className="overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-2 bg-muted/30 border-b">
                        <div className="flex items-center gap-2">
                          <Box className="h-4 w-4 text-primary" />
                          <span className="font-semibold text-sm">
                            {product?.name || "Unknown Product"}
                          </span>
                          <span className="text-[10px] text-muted-foreground uppercase">
                            - Part #{index + 1}
                          </span>
                        </div>
                        <Badge variant="secondary" className="font-mono">
                          {currencySymbol}
                          {totalCost.toFixed(2)}
                        </Badge>
                      </div>
                      <CardContent className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="grid grid-cols-2 gap-4 col-span-1">
                            <div className="space-y-1">
                              <span className="text-[10px] font-bold uppercase text-muted-foreground block">
                                Quantity
                              </span>
                              <span className="text-sm font-medium">
                                {entry.quantity_used}
                              </span>
                            </div>
                            <div className="space-y-1">
                              <span className="text-[10px] font-bold uppercase text-muted-foreground block">
                                Unit Cost
                              </span>
                              <span className="text-sm font-mono tracking-tight">
                                {currencySymbol}
                                {parseFloat(entry.unit_cost || 0).toFixed(2)}
                              </span>
                            </div>
                          </div>

                          <div className="space-y-1 col-span-1 md:col-span-2">
                            <span className="text-[10px] font-bold uppercase text-muted-foreground block">
                              Tracking / Inventory Details
                            </span>
                            <div className="flex flex-wrap items-center gap-1.5">
                              {(entry.inventory_batch?.batch_number ||
                                entry.inventory_batch_number ||
                                entry.batch?.batch_number) && (
                                  <Badge
                                    variant="outline"
                                    className="gap-1 bg-amber-50 text-amber-700 border-amber-300 text-[11px] font-mono"
                                  >
                                    <Layers size={11} /> Batch:{" "}
                                    {entry.inventory_batch?.batch_number ||
                                      entry.inventory_batch_number ||
                                      entry.batch?.batch_number}
                                  </Badge>
                                )}

                              {entry.serial_numbers &&
                                entry.serial_numbers.length > 0 ? (
                                entry.serial_numbers.map((sn, snIdx) => (
                                  <Badge
                                    key={snIdx}
                                    variant="outline"
                                    className="bg-muted/50 text-[11px] py-0 px-2 font-mono"
                                  >
                                    {typeof sn === "string" ? sn : sn.serial_number}
                                  </Badge>
                                ))
                              ) : !(
                                entry.inventory_batch?.batch_number ||
                                entry.inventory_batch_number ||
                                entry.batch?.batch_number
                              ) ? (
                                <span className="text-xs text-muted-foreground italic">
                                  Standard inventory (no serials/batch)
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </div>

                        {entry.notes && (
                          <div className="mt-4 pt-3 border-t">
                            <span className="text-[10px] font-bold uppercase text-muted-foreground block mb-1">
                              Usage Notes
                            </span>
                            <div
                              className="text-sm text-balance bg-muted/20 p-2 rounded italic border-l-2 border-primary"
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(entry.notes || ""),
                              }}
                            />
                          </div>
                        )}
                        <div className="mt-2 text-[10px] text-muted-foreground/60 text-right">
                          Added:{" "}
                          {formatDate(entry.created_at)}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </ScrollArea>

            <Card className="bg-primary text-primary-foreground border-none">
              <CardContent className="p-4">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="space-y-1">
                    <div className="flex items-center justify-center gap-1.5 opacity-80">
                      <Hash size={14} />
                      <span className="text-[10px] font-bold uppercase">
                        Total Items
                      </span>
                    </div>
                    <p className="text-2xl font-bold">{partsEntries.length}</p>
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center justify-center gap-1.5 opacity-80">
                      <DollarSign size={14} />
                      <span className="text-[10px] font-bold uppercase">
                        Total Value
                      </span>
                    </div>
                    <p className="text-2xl font-bold">
                      {currencySymbol}
                      {partsEntries
                        .reduce(
                          (total, entry) =>
                            total +
                            parseFloat(entry.quantity_used) *
                            parseFloat(entry.unit_cost || 0),
                          0,
                        )
                        .toFixed(2)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Modal>
  );
};

PartsViewModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  selectedRepairJob: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    item_description: PropTypes.string,
    status: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }),
  partsEntries: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      product_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      quantity_used: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      unit_cost: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      created_at: PropTypes.string,
      serial_numbers: PropTypes.array,
      notes: PropTypes.string,
    }),
  ).isRequired,
  currencySymbol: PropTypes.string.isRequired,
  repairJobs: PropTypes.array.isRequired,
  handleAddParts: PropTypes.func.isRequired,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
    }),
  ).isRequired,
};

export default PartsViewModal;
